import { openDB } from 'idb';

const DB_NAME = 'pos-offline';
const DB_VERSION = 3;
let _db = null;

export async function getDb() {
  if (_db) return _db;
  _db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('offlineQueue')) {
        const qs = db.createObjectStore('offlineQueue', { keyPath: 'localId' });
        qs.createIndex('by_created', 'createdAt');
        qs.createIndex('by_status', 'status');
      }
      if (!db.objectStoreNames.contains('userCache')) {
        db.createObjectStore('userCache', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('offlineWindows')) {
        const ow = db.createObjectStore('offlineWindows', { keyPath: 'windowId' });
        ow.createIndex('by_start', 'startedAt');
      }
      // v2: full parsed-catalog snapshot so the app can load when the backend is down/unhealthy
      if (!db.objectStoreNames.contains('productSnapshot')) {
        db.createObjectStore('productSnapshot', { keyPath: 'key' });
      }
      // v3: per-user offline-login credentials (PBKDF2 PIN hash + that user's refresh
      // token), so a user provisioned online can log in while the backend is unreachable.
      if (!db.objectStoreNames.contains('offlineCredentials')) {
        db.createObjectStore('offlineCredentials', { keyPath: 'userId' });
      }
    },
  });
  return _db;
}

// --- productSnapshot (catalog fallback for offline / unhealthy backend) ---

export async function saveProductSnapshot(data) {
  const db = await getDb();
  return db.put('productSnapshot', { key: 'catalog', ...data, savedAt: Date.now() });
}

export async function loadProductSnapshot() {
  const db = await getDb();
  return db.get('productSnapshot', 'catalog');
}

// --- offlineQueue ---

export async function enqueueItem(item) {
  const db = await getDb();
  return db.put('offlineQueue', item);
}

export async function getAllQueuedItems() {
  const db = await getDb();
  return db.getAllFromIndex('offlineQueue', 'by_created');
}

export async function updateQueuedItemStatus(localId, status, extra = {}) {
  const db = await getDb();
  const item = await db.get('offlineQueue', localId);
  if (!item) return;
  Object.assign(item, { status, ...extra });
  return db.put('offlineQueue', item);
}

export async function removeQueuedItem(localId) {
  const db = await getDb();
  return db.delete('offlineQueue', localId);
}

// --- offlineWindows (local audit log of offline periods) ---

export async function saveOfflineWindow(window) {
  const db = await getDb();
  return db.put('offlineWindows', window);
}

export async function getOfflineWindows() {
  const db = await getDb();
  return db.getAllFromIndex('offlineWindows', 'by_start');
}

export async function updateOfflineWindow(windowId, patch) {
  const db = await getDb();
  const existing = await db.get('offlineWindows', windowId);
  if (!existing) return;
  Object.assign(existing, patch);
  return db.put('offlineWindows', existing);
}

// --- userCache ---

export async function saveUserCache(users) {
  const db = await getDb();
  const tx = db.transaction('userCache', 'readwrite');
  await tx.store.clear();
  for (const u of users) await tx.store.put(u);
  await tx.done;
}

export async function loadUserCache() {
  const db = await getDb();
  return db.getAll('userCache');
}
